import React, { useMemo, useState } from 'react';
import { Modal, Form, Input, InputNumber, DatePicker, Button, Select, Radio, Card, Row, Col, Divider, message } from 'antd';
import { collection, doc, setDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import { db } from '../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import FileAttachments from './FileAttachments';

const { Option } = Select;

const ParayanaFormModal = ({ visible, onCancel, onSuccess, editingParayana }) => {
  const [form] = Form.useForm();
  const { getUserId, getUserEmail } = useAuth();
  const [targetType, setTargetType] = useState('one-time');
  const [recurrenceType, setRecurrenceType] = useState('daily');
  const [attachments, setAttachments] = useState([]);

  // Convert editingParayana data to form-compatible format
  const initialValues = useMemo(() => {
    if (!editingParayana) {
      setAttachments([]);
      return {
        targetType: 'one-time',
        recurrenceType: 'daily'
      };
    }
    
    // Set attachments if editing
    setAttachments(editingParayana.attachments || []);
    
    return {
      ...editingParayana,
      startDate: editingParayana.startDate ? dayjs(editingParayana.startDate.toDate()) : null,
      targetCompletionDate: editingParayana.targetCompletionDate ? dayjs(editingParayana.targetCompletionDate.toDate()) : null,
      targetType: editingParayana.targetType || 'one-time',
      recurrenceType: editingParayana.recurrenceType || 'daily',
      recurrenceInterval: editingParayana.recurrenceInterval || 1,
      weeklyDays: editingParayana.weeklyDays || [],
      monthlyDay: editingParayana.monthlyDay || 1
    };
  }, [editingParayana]);

  const handleSubmit = async (values) => {
    const userId = getUserId();
    if (!userId) {
      message.error('User not authenticated');
      return;
    }

    try {
      console.log('💾 Submitting parayana form:', values);
      
      // Validate form data
      const validationErrors = validateFormData(values);
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => message.error(error));
        return;
      }

      // Sanitize and prepare data
      const parayanaData = {
        ...values,
        name: values.name.trim(),
        targetCount: Math.max(1, Math.min(999999, parseInt(values.targetCount))), // Clamp between 1-999999
        startDate: values.startDate.toDate(),
        targetCompletionDate: values.targetCompletionDate ? values.targetCompletionDate.toDate() : null,
        uid: userId,
        createdAt: editingParayana ? (editingParayana.createdAt || serverTimestamp()) : serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentCount: editingParayana?.currentCount || 0,
        // File attachments
        attachments: attachments,
        // Reset progress for recurring parayanas
        lastResetDate: values.targetType === 'recurring' ? values.startDate.toDate() : null,
        // Calculate next due date for recurring parayanas
        nextDueDate: values.targetType === 'recurring' ? calculateNextDueDate(values) : null,
        // Add metadata for debugging and analytics
        version: '1.0',
        createdBy: 'form',
        email: getUserEmail() || ''
      };

      console.log('📝 Processed parayana data:', parayanaData);

      if (editingParayana) {
        console.log('✏️ Updating existing parayana:', editingParayana.id);
        await setDoc(doc(db, `users/${userId}/parayanas`, editingParayana.id), parayanaData);
        message.success(`"${parayanaData.name}" updated successfully! ✨`);
      } else {
        console.log('➕ Creating new parayana');
        const docRef = await addDoc(collection(db, `users/${userId}/parayanas`), parayanaData);
        console.log('✅ Created parayana with ID:', docRef.id);
        message.success(`"${parayanaData.name}" created successfully! 🕉️`);
      }

      form.resetFields();
      setAttachments([]);
      onSuccess();
      
    } catch (error) {
      console.error('💥 Error submitting parayana:', error);
      
      // Provide specific error messages
      if (error.code === 'permission-denied') {
        message.error('Permission denied. Please check your authentication.');
      } else if (error.code === 'quota-exceeded') {
        message.error('Storage quota exceeded. Please try again later.');
      } else if (error.code === 'unavailable') {
        message.error('Service temporarily unavailable. Please try again.');
      } else if (error.message.includes('network')) {
        message.error('Network error. Please check your connection and try again.');
      } else {
        message.error(`Failed to save: ${error.message}`);
      }
    }
  };

  // Form validation function
  const validateFormData = (values) => {
    const errors = [];

    // Name validation
    if (!values.name || values.name.trim().length === 0) {
      errors.push('Parayana name is required');
    } else if (values.name.trim().length > 100) {
      errors.push('Parayana name must be less than 100 characters');
    }

    // Target count validation
    if (!values.targetCount || values.targetCount < 1) {
      errors.push('Target count must be at least 1');
    } else if (values.targetCount > 999999) {
      errors.push('Target count must be less than 1,000,000');
    }

    // Date validation
    if (!values.startDate) {
      errors.push('Start date is required');
    } else if (values.startDate.isAfter(dayjs().add(1, 'year'))) {
      errors.push('Start date cannot be more than 1 year in the future');
    }

    // Target completion date validation
    if (values.targetCompletionDate) {
      if (values.targetCompletionDate.isBefore(values.startDate)) {
        errors.push('Target completion date must be after start date');
      } else if (values.targetCompletionDate.isAfter(dayjs().add(10, 'year'))) {
        errors.push('Target completion date is too far in the future');
      }
    }

    // Recurring parayana validation
    if (values.targetType === 'recurring') {
      if (values.recurrenceType === 'weekly' && (!values.weeklyDays || values.weeklyDays.length === 0)) {
        errors.push('Please select at least one day for weekly recurrence');
      }
      
      if (values.recurrenceInterval && (values.recurrenceInterval < 1 || values.recurrenceInterval > 365)) {
        errors.push('Recurrence interval must be between 1 and 365');
      }
      
      if (values.recurrenceType === 'monthly' && values.monthlyDay && (values.monthlyDay < 1 || values.monthlyDay > 31)) {
        errors.push('Monthly day must be between 1 and 31');
      }
    }

    return errors;
  };

  const calculateNextDueDate = (values) => {
    const startDate = values.startDate.toDate();
    const interval = values.recurrenceInterval || 1;
    
    switch (values.recurrenceType) {
      case 'daily':
        return new Date(startDate.getTime() + (interval * 24 * 60 * 60 * 1000));
      case 'weekly':
        return new Date(startDate.getTime() + (interval * 7 * 24 * 60 * 60 * 1000));
      case 'monthly':
        const nextMonth = new Date(startDate);
        nextMonth.setMonth(nextMonth.getMonth() + interval);
        return nextMonth;
      default:
        return startDate;
    }
  };

  const renderRecurrenceOptions = () => {
    if (targetType !== 'recurring') return null;

    return (
      <Card 
        title="🔄 Recurrence Settings" 
        size="small" 
        style={{ 
          marginTop: '16px',
          border: '1px solid #FFB347',
          borderRadius: '8px'
        }}
      >
        <Form.Item
          name="recurrenceType"
          label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Repeat Type</span>}
          rules={[{ required: true, message: 'Please select recurrence type' }]}
        >
          <Radio.Group onChange={(e) => setRecurrenceType(e.target.value)}>
            <Radio value="daily">Daily</Radio>
            <Radio value="weekly">Weekly</Radio>
            <Radio value="monthly">Monthly</Radio>
          </Radio.Group>
        </Form.Item>

        {recurrenceType === 'daily' && (
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="dailyTargetCount"
                label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Daily Target Count</span>}
                rules={[{ required: true, message: 'Please enter daily target' }]}
              >
                <InputNumber 
                  min={1} 
                  style={{ width: '100%' }}
                  placeholder="Daily count target"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {recurrenceType === 'weekly' && (
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="recurrenceInterval"
                label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Every X Weeks</span>}
                rules={[{ required: true, message: 'Please enter interval' }]}
              >
                <InputNumber 
                  min={1} 
                  max={12}
                  style={{ width: '100%' }}
                  placeholder="Every X weeks"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="weeklyDays"
                label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Days of Week</span>}
                rules={[{ required: true, message: 'Please select days' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select days"
                  style={{ width: '100%' }}
                >
                  <Option value={0}>Sunday</Option>
                  <Option value={1}>Monday</Option>
                  <Option value={2}>Tuesday</Option>
                  <Option value={3}>Wednesday</Option>
                  <Option value={4}>Thursday</Option>
                  <Option value={5}>Friday</Option>
                  <Option value={6}>Saturday</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}
        
        {recurrenceType === 'monthly' && (
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="recurrenceInterval"
                label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Every X Months</span>}
                rules={[{ required: true, message: 'Please enter interval' }]}
              >
                <InputNumber 
                  min={1} 
                  max={12}
                  style={{ width: '100%' }}
                  placeholder="Every X months"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="monthlyDay"
                label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Day of Month</span>}
                rules={[{ required: true, message: 'Please select day' }]}
              >
                <InputNumber 
                  min={1} 
                  max={31}
                  style={{ width: '100%' }}
                  placeholder="Day of month"
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>
    );
  };

  return (
    <Modal
      title={
        <div style={{ 
          color: '#FF8C00', 
          fontSize: '20px', 
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          🕉️ {editingParayana ? 'Edit Parayana' : 'Add New Parayana'}
        </div>
      }
      open={visible}
      onCancel={() => {
        onCancel();
        setAttachments([]);
        form.resetFields();
      }}
      footer={null}
      width={700}
      style={{ top: 20 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        key={editingParayana?.id || 'new'} // Force re-render when editing different parayana
        style={{ paddingTop: '20px' }}
      >
        <Form.Item
          name="name"
          label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Parayana Name</span>}
          rules={[
            { required: true, message: 'Please enter parayana name' },
            { max: 100, message: 'Name must be less than 100 characters' },
            { min: 2, message: 'Name must be at least 2 characters' },
            { 
              pattern: /^[a-zA-Z0-9\s\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0D80-\u0DFF\u0E00-\u0E7F\u0F00-\u0FFF]+$/, 
              message: 'Only letters, numbers, spaces, and Indian language characters allowed' 
            }
          ]}
          hasFeedback
        >
          <Input 
            placeholder="e.g. Hanuman Chalisa, Gayatri Mantra"
            style={{ height: '44px' }}
            maxLength={100}
            showCount
            onChange={(e) => {
              // Auto-capitalize first letter
              const value = e.target.value;
              if (value.length === 1) {
                e.target.value = value.charAt(0).toUpperCase() + value.slice(1);
                form.setFieldValue('name', e.target.value);
              }
            }}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="targetCount"
              label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Target Count</span>}
              rules={[
                { required: true, message: 'Please enter target count' },
                { type: 'number', min: 1, message: 'Count must be at least 1' },
                { type: 'number', max: 999999, message: 'Count must be less than 1,000,000' }
              ]}
              hasFeedback
            >
              <InputNumber 
                min={1} 
                max={999999}
                style={{ width: '100%', height: '44px' }}
                placeholder="e.g. 108, 1000, 11000"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                onChange={(value) => {
                  // Suggest common counts
                  if (value && [108, 1008, 10008, 100008].includes(value)) {
                    message.info(`${value} is an auspicious count! 🙏`, 2);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="targetType"
              label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Target Type</span>}
              rules={[{ required: true, message: 'Please select target type' }]}
              hasFeedback
            >
              <Select 
                style={{ height: '44px' }}
                onChange={setTargetType}
                placeholder="Select target type"
              >
                <Option value="one-time">🎯 One-time (Complete once)</Option>
                <Option value="recurring">🔄 Recurring (Daily/Weekly/Monthly)</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="startDate"
              label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Start Date</span>}
              rules={[
                { required: true, message: 'Please select start date' },
                {
                  validator: (_, value) => {
                    if (value && value.isAfter(dayjs().add(1, 'year'))) {
                      return Promise.reject('Start date cannot be more than 1 year in the future');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              hasFeedback
            >
              <DatePicker 
                style={{ width: '100%', height: '44px' }}
                placeholder="Select start date"
                disabledDate={(current) => {
                  // Disable dates more than 1 year in future
                  return current && current > dayjs().add(1, 'year');
                }}
                showToday
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="targetCompletionDate"
              label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Target Completion Date (Optional)</span>}
              dependencies={['startDate']}
              rules={[
                {
                  validator: (_, value) => {
                    const startDate = form.getFieldValue('startDate');
                    if (value && startDate && value.isBefore(startDate)) {
                      return Promise.reject('Must be after start date');
                    }
                    if (value && value.isAfter(dayjs().add(10, 'year'))) {
                      return Promise.reject('Cannot be more than 10 years in future');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              hasFeedback
            >
              <DatePicker 
                style={{ width: '100%', height: '44px' }}
                placeholder="Select target completion date"
                disabledDate={(current) => {
                  const startDate = form.getFieldValue('startDate');
                  // Disable dates before start date or more than 10 years in future
                  return (startDate && current && current <= startDate) || 
                         (current && current > dayjs().add(10, 'year'));
                }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
        </Row>

        {renderRecurrenceOptions()}

        <Divider />

        {/* File Attachments Section */}
        <FileAttachments
          parayanaId={editingParayana?.id || 'new'}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          currentCount={editingParayana?.currentCount || 0}
          targetCount={form.getFieldValue('targetCount') || editingParayana?.targetCount || 1}
          parayanaName={form.getFieldValue('name') || editingParayana?.name || 'New Parayana'}
        />

        <Divider />

        <Form.Item style={{ marginTop: '32px', marginBottom: '0' }}>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button 
              onClick={() => {
                onCancel();
                setAttachments([]);
                form.resetFields();
              }}
              style={{ 
                height: '44px',
                width: '120px',
                borderColor: '#FFB347',
                color: '#FF8C00'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              style={{
                height: '44px',
                width: '120px',
                background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                borderColor: '#FF8C00',
                fontWeight: '500'
              }}
            >
              {editingParayana ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ParayanaFormModal; 